import axios from "axios";
import { useEffect, useState } from "react";
import { MdAccountBox } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function Redem_Acc() {
   const [form, setForm] = useState({
      centerName: "",
      businessName: "",
      email: "",
      phone: "",
      country: "",
      address: "",
      city: "",
      borough: "",
      state: "NY",
      zip: "",
      ein: "",
      password: "",
      confirmPassword: "",
   });

   const [loading, setLoading] = useState(false);
   const [zipCheckResult, setZipCheckResult] = useState(null);
   const [submitting, setSubmitting] = useState(false);
   const rc_id = sessionStorage.getItem("rc_id");

   useEffect(() => {
      if (!rc_id) {
         toast.error("No Redemption Center ID found. Please log in again.");
         return;
      }
      const fetchDetails = async () => {
         try {
            setLoading(true);
            const response = await axios.get(`/auth/redemption-center/${rc_id}`);
            const data = response.data;
            setForm((prev) => ({
               ...prev,
               centerName: data.center_name || "",
               businessName: data.business_name || "",
               email: data.email || "",
               phone: data.phone || "",
               country: data.country || "",
               address: data.address || "",
               city: data.city || "",
               borough: data.borough || "",
               state: data.state || "NY",
               zip: data.zip || "",
               ein: data.ein || "",
            }));
         } catch (error) {
            console.error("Fetch Error:", error);
            toast.error("Failed to fetch account details.");
         } finally {
            setLoading(false);
         }
      };
      fetchDetails();
   }, [rc_id]);

   const handleChange = (e) => {
      const { id, value } = e.target;
      setForm((prev) => ({ ...prev, [id]: value }));
      if (id === "zip" || id === "borough") setZipCheckResult(null);
   };

   const handleZipCheck = async () => {
      try {
         const res = await axios.post("/auth/check-service-area", {
            state: form.state,
            borough: form.borough,
            zipcode: form.zip,
         });
         setZipCheckResult({ available: true, message: res.data.message });
         toast.success(res.data.message);
      } catch (err) {
         const msg = err.response?.data?.message || "Error checking ZIP availability.";
         setZipCheckResult({ available: false, message: msg });
         toast.error(msg);
      }
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      if (!rc_id) return toast.error("No Redemption Center ID found.");

      if (form.password && form.password !== form.confirmPassword) {
         return toast.error("Passwords do not match!");
      }

      if (!zipCheckResult) {
         toast.error("Please check ZIP availability before submitting.");
         return;
      }
      if (!zipCheckResult.available) {
         toast.error("ZIP code is not available for service.");
         return;
      }

      try {
         setSubmitting(true);
         await axios.put(`/auth/redemption-center/${rc_id}`, form);
         toast.success("Account details updated successfully!");
         setForm((prev) => ({ ...prev, password: "", confirmPassword: "" })); // clear password fields
      } catch (error) {
         console.error(error);
         toast.error("Failed to update details.");
      } finally {
         setSubmitting(false);
      }
   };

   if (loading) {
      return <div className="text-center py-10 text-green-600 font-medium">Loading account details...</div>;
   }

   return (
      <section className="px-4 sm:px-6 lg:px-8 py-8">
         <ToastContainer />
         <div className="bg-white max-w-4xl mx-auto p-6 sm:p-8 rounded-2xl shadow-lg">
            <h1 className="text-3xl font-bold text-center text-green-700 mb-8 flex items-center justify-center gap-2">
               <MdAccountBox className="text-3xl" />
               My Account
            </h1>

            <form className="space-y-6" onSubmit={handleSubmit}>
               {/* Center & Business Name */}
               <div className="grid gap-4 sm:grid-cols-2">
                  <Input label="Redemption Center Name *" id="centerName" value={form.centerName} onChange={handleChange} required />
                  <Input label="Business / Legal Name *" id="businessName" value={form.businessName} onChange={handleChange} required />
               </div>

               <div>
                  <Input label="Ein / Business Registration Number *" id="ein" value={form.ein} onChange={handleChange} required />
               </div>

               {/* Email & Phone */}
               <div className="grid gap-4 sm:grid-cols-2">
                  <Input label="Email *" id="email" type="email" value={form.email} onChange={handleChange} required />
                  <Input label="Phone Number *" id="phone" value={form.phone} onChange={handleChange} required />
               </div>

               {/* Country & address */}
               <div className="grid gap-4 sm:grid-cols-2">
                  <Select label="Country" id="country" value={form.country} onChange={handleChange} options={["India", "USA", "UK"]} />
                  <Input label="Street Address" id="address" value={form.address} onChange={handleChange} />
               </div>

               {/* City & Borough */}
               <div className="grid gap-4 sm:grid-cols-2">
                  <Input label="City" id="city" value={form.city} onChange={handleChange} required />
                  <Select label="Borough" id="borough" value={form.borough} onChange={handleChange} options={["Bronx", "Queens", "Manhattan", "Brooklyn", "Staten Island"]} />
               </div>

               {/* State, ZIP & Check ZIP */}
               <div className="grid gap-4 sm:grid-cols-3">
                  <Input label="State" id="state" value={form.state} readOnly />
                  <Input label="ZIP Code *" id="zip" value={form.zip} onChange={handleChange} required />
                  <div className="flex items-end">
                     <button type="button" onClick={handleZipCheck} className="w-full px-4 py-2 bg-[#54BE73] text-white rounded-md hover:bg-green-700">
                        Check Availability
                     </button>
                  </div>
               </div>

               {/* Optional Password Change */}
               <div className="grid gap-4 sm:grid-cols-2">
                  <Input label="New Password (Optional)" id="password" type="password" value={form.password} onChange={handleChange} />
                  <Input label="Confirm New Password" id="confirmPassword" type="password" value={form.confirmPassword} onChange={handleChange} />
               </div>

               <div className="flex flex-wrap justify-end gap-4">
                  <button type="reset" className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-black font-semibold rounded-3xl">
                     RESET
                  </button>
                  <button
                     type="submit"
                     disabled={submitting}
                     className={`px-6 py-3 text-white font-semibold rounded-3xl ${submitting ? "bg-gray-400 cursor-not-allowed" : "bg-[#54BE73] hover:bg-green-700"}`}
                  >
                     {submitting ? "Updating..." : "SAVING MY CHANGES"}
                  </button>
               </div>
            </form>
         </div>
      </section>
   );
}

const Input = ({ label, id, type = "text", value, onChange, readOnly, required }) => (
   <div>
      <label htmlFor={id} className="text-sm block mb-1 font-medium">
         {label}
      </label>
      <input
         id={id}
         type={type}
         value={value}
         onChange={onChange}
         readOnly={readOnly}
         required={required}
         className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
      />
   </div>
);

const Select = ({ label, id, value, onChange, options }) => (
   <div>
      <label htmlFor={id} className="block mb-1 font-medium">
         {label}
      </label>
      <select
         id={id}
         value={value}
         onChange={onChange}
         className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
      >
         <option value="">Select {label}</option>
         {options.map((opt) => (
            <option key={opt} value={opt}>
               {opt}
            </option>
         ))}
      </select>
   </div>
);
